import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { matchesSearch } from '../../utils/searchFilter';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import * as accessoryGroupApi from '../../api/accessoryGroupApi';
import * as departmentApi from '../../api/departmentApi';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function AccessoryGroupsTab() {
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState('');
  const [filterGroupCode, setFilterGroupCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ groupCode: '', departmentId: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA') || hasPermission('MANAGE_ACCESSORIES');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('MANAGE_MASTER_DATA') || hasPermission('MANAGE_ACCESSORIES') || hasPermission('DELETE_ACCESSORIES');

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = filterDept ? { departmentId: filterDept } : {};
      const [g, d] = await Promise.all([accessoryGroupApi.getAll(params), departmentApi.getAll()]);
      setGroups(g); setDepartments(d);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filterDept, showSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Client-side group code filter — defined before helpers that reference it
  const displayGroups = useMemo(() => {
    if (!filterGroupCode.trim()) return groups;
    return groups.filter((g) => matchesSearch(g.groupCode, filterGroupCode));
  }, [groups, filterGroupCode]);

  const toggleRow = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => displayGroups.every((g) => selectedIds.has(g.id)) ? setSelectedIds(new Set()) : setSelectedIds(new Set(displayGroups.map((g) => g.id)));
  const allSelected = displayGroups.length > 0 && displayGroups.every((g) => selectedIds.has(g.id));
  const someSelected = displayGroups.some((g) => selectedIds.has(g.id)) && !allSelected;

  const openCreate = () => { setEditItem(null); setForm({ groupCode: '', departmentId: '' }); setDialogOpen(true); };
  const openEdit = (row) => { setEditItem(row); setForm({ groupCode: row.groupCode, departmentId: row.departmentId }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.groupCode.trim() || !form.departmentId) return;
    setSaving(true);
    try {
      if (editItem) { await accessoryGroupApi.update(editItem.id, form); showSnackbar('Accessory Group updated', 'success'); }
      else { await accessoryGroupApi.create(form); showSnackbar('Accessory Group created', 'success'); }
      setDialogOpen(false); load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await accessoryGroupApi.remove(deleteTarget.id);
      showSnackbar('Accessory Group deleted', 'success');
      setDeleteTarget(null); load();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      const res = await accessoryGroupApi.batchRemove([...selectedIds]);
      showSnackbar(res.message || `${selectedIds.size} group(s) deleted.`, 'success');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Department</InputLabel>
            <Select value={filterDept} label="Filter by Department"
              onChange={(e) => { setFilterDept(e.target.value); setSelectedIds(new Set()); }}>
              <MenuItem value="">All Departments</MenuItem>
              {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search group code…"
            value={filterGroupCode}
            onChange={(e) => { setFilterGroupCode(e.target.value); setSelectedIds(new Set()); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /></InputAdornment> }}
            inputProps={{ autoComplete: 'off' }}
            sx={{ minWidth: 190 }}
          />

          {(filterDept || filterGroupCode) && (
            <Button size="small" variant="text" sx={{ color: '#9CA3AF', minWidth: 0 }}
              onClick={() => { setFilterDept(''); setFilterGroupCode(''); setSelectedIds(new Set()); }}>
              Clear
            </Button>
          )}
          {(filterDept || filterGroupCode) && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              {displayGroups.length} of {groups.length} group{groups.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Accessory Group</Button>
        )}
      </Box>

      {/* Bulk action toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} row(s) selected</Typography>
          <Button variant="contained" color="error" size="small" startIcon={<DeleteSweepIcon />} onClick={() => setBulkDeleteOpen(true)}>
            Delete Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>Clear</Button>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#F5F5F5' }}>
            {canDelete && (
              <TableCell padding="checkbox">
                <Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={toggleAll} />
              </TableCell>
            )}
            <TableCell>#</TableCell>
            <TableCell>Group Code</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Accessories</TableCell>
            {(canManage || canDelete) && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayGroups.map((g, i) => (
            <TableRow key={g.id} hover selected={selectedIds.has(g.id)} sx={{ bgcolor: selectedIds.has(g.id) ? '#FFF8E1 !important' : undefined }}>
              {canDelete && (
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={selectedIds.has(g.id)} onChange={() => toggleRow(g.id)} />
                </TableCell>
              )}
              <TableCell>{i + 1}</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1565C0' }}>{g.groupCode}</TableCell>
              <TableCell>{g.department?.name}</TableCell>
              <TableCell><Chip label={g._count?.accessories || 0} size="small" /></TableCell>
              {(canManage || canDelete) && (
                <TableCell align="right">
                  {canManage && (
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(g)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete (cascades all accessories and prices)">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(g)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {displayGroups.length === 0 && (
            <TableRow><TableCell colSpan={canDelete ? 6 : 5} align="center" sx={{ py: 4, color: '#9e9e9e' }}>
              {filterGroupCode ? `No groups matching "${filterGroupCode}"` : 'No accessory groups found'}
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Accessory Group' : 'New Accessory Group'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Group Code" fullWidth value={form.groupCode}
            onChange={(e) => setForm({ ...form, groupCode: e.target.value })} />
          <FormControl fullWidth>
            <InputLabel>Department</InputLabel>
            <Select value={form.departmentId} label="Department" onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
              {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!form.groupCode.trim() || !form.departmentId || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Accessory Group"
        message={`Delete group "${deleteTarget?.groupCode}"?\n\nThis will permanently remove all accessories, prices, and price history in this group.\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} Group(s)`}
        message={`Delete ${selectedIds.size} selected group(s) and ALL their accessories, prices, and history?\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </Box>
  );
}
